import { BorderedContainer } from 'core/components/common/BorderedContainer';
import { IncidentDetailPatientForSendai } from 'sections/sendai/features/useIncidentDetailForSendai';
import { SectionTitle } from 'core/components/common/SectionTitle';
import { InfoDisplay } from 'core/components/JianDetail/common/InfoDisplay';
import { convertToPresenceAbsence } from 'core/components/common/converters';
import { formatDateTime } from 'core/utils/date';
import { FC } from 'react';

type Props = {
  findingsEmsContact: IncidentDetailPatientForSendai['findingsEmsContact'];
};

export const FindingsEmsContactView: FC<Props> = ({ findingsEmsContact }) => {
  if (!findingsEmsContact) {
    return (
      <BorderedContainer>
        <div className="flex flex-col gap-[24px]">
          <SectionTitle text="接触時の所見" />
          <p className="text-gray-500">データが存在しません</p>
        </div>
      </BorderedContainer>
    );
  }

  const reveals = findingsEmsContact.reveals;
  const toNormalAbnormal = (value: boolean | null | undefined) => {
    if (value === true) return '異';
    if (value === false) return '正';
    return '';
  };

  return (
    <BorderedContainer>
      <div className="flex flex-col gap-[24px]">
        <SectionTitle text="接触時の所見" />
        {/* 体位、顔貌、表情 */}
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="体位"
            content={findingsEmsContact.position ?? ''}
          />
          <InfoDisplay
            title="顔貌"
            content={findingsEmsContact.facialAppearance ?? ''}
          />
          <InfoDisplay
            title="表情"
            content={findingsEmsContact.facialExpression ?? ''}
          />
        </div>
        {/* その他コメント */}
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="体位(その他)"
            content={findingsEmsContact.positionComment ?? ''}
          />
          <InfoDisplay
            title="顔貌(その他)"
            content={findingsEmsContact.facialAppearanceComment ?? ''}
          />
          <InfoDisplay
            title="表情(その他)"
            content={findingsEmsContact.facialExpressionComment ?? ''}
          />
        </div>
        {/* 死亡の兆候 */}
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="死亡の兆候"
            content={convertToPresenceAbsence(findingsEmsContact.deathSign)}
          />
          <InfoDisplay
            title="死亡兆候(硬直)"
            content={findingsEmsContact.deathSignRigorMortis ?? ''}
          />
          <InfoDisplay
            title="死亡兆候(死斑)"
            content={findingsEmsContact.deathSignLivorMortis ?? ''}
          />
          <InfoDisplay
            title="死亡兆候(他の内容)"
            content={findingsEmsContact.deathSignOther ?? ''}
          />
        </div>
        {/* 所見項目（reveals） */}
        {reveals && (
          <>
            {/* 自力歩行、出血、変形 */}
            <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
              <InfoDisplay
                title="自力歩行"
                content={convertToPresenceAbsence(
                  reveals.hasIndependentWalking
                )}
              />
              <InfoDisplay
                title="出血"
                content={convertToPresenceAbsence(reveals.hasBleeding)}
              />
              <InfoDisplay
                title="変形"
                content={convertToPresenceAbsence(reveals.hasDeformation)}
              />
            </div>
            {/* 嘔吐、麻痺 */}
            <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
              <InfoDisplay
                title="嘔吐"
                content={convertToPresenceAbsence(reveals.hasVomiting)}
              />
              <InfoDisplay
                title="呼気"
                content={convertToPresenceAbsence(reveals.hasNausea)}
              />
              <InfoDisplay
                title="麻痺"
                content={convertToPresenceAbsence(reveals.hasParalysis)}
              />
            </div>
            {/* 失禁、熱傷 */}
            <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
              <InfoDisplay
                title="失禁"
                content={convertToPresenceAbsence(reveals.hasIncontinence)}
              />
              <InfoDisplay
                title="熱傷"
                content={convertToPresenceAbsence(reveals.hasBurn)}
              />
            </div>
            {/* 最終健在確認日時 */}
            <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
              <InfoDisplay
                title="最終健在確認日時"
                content={
                  reveals.lastKnownAt
                    ? formatDateTime(reveals.lastKnownAt, 'yyyy/MM/dd HH:mm')
                    : ''
                }
              />
            </div>
            {/* 顔面の下垂、上肢の動揺、言語 */}
            <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
              <InfoDisplay
                title="顔面の下垂"
                content={toNormalAbnormal(reveals.hasFacialDroop)}
              />
              <InfoDisplay
                title="上肢の動揺"
                content={toNormalAbnormal(reveals.hasUpperLimbTremor)}
              />
              <InfoDisplay
                title="言語"
                content={toNormalAbnormal(reveals.hasSpeech)}
              />
            </div>
          </>
        )}
      </div>
    </BorderedContainer>
  );
};
